import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { enrollerAdminService, type MetabaseResource } from "@/services/ea/enrollerAdminService";

const Dashboard = () => {
  useEffect(() => {
    document.title = "Dashboard | Admin";
  }, []);
  const { user, token, hasuraToken } = useAuth();
  const [metabaseResources, setMetabaseResources] = useState<MetabaseResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<MetabaseResource | null>(null);
  const [metabaseToken, setMetabaseToken] = useState<string | null>(null);
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);

  // Fetch available Metabase resources on mount
  useEffect(() => {
    const fetchMetabaseResources = async () => {
      if (!user || !token || !hasuraToken) return;

      setIsLoadingResources(true);
      try {
        // Update service context with user info
        enrollerAdminService.updateContext({
          userId: user.id,
          token: token,
          hasuraToken: hasuraToken
        });

        // Fetch available resources
        const response = await enrollerAdminService.getMetabaseResources();

        if (response.success && response.resources.length > 0) {
          setMetabaseResources(response.resources);
          // Auto-select first resource
          setSelectedResource(response.resources[0]);
        }
      } catch (error) {
        console.error('❌ Error fetching Metabase resources:', error);
      } finally {
        setIsLoadingResources(false);
      }
    };

    fetchMetabaseResources();
  }, [user, token, hasuraToken]);

  // Fetch token when selected resource changes
  useEffect(() => {
    const fetchMetabaseToken = async () => {
      if (!user || !token || !selectedResource) return;

      setIsLoadingToken(true);
      setMetabaseToken(null); // Clear previous token
      
      try {
        // Fetch Metabase token for selected resource
        const response = await enrollerAdminService.getMetabaseToken({
          type: selectedResource.resource_type as 'question' | 'dashboard',
          id: selectedResource.resource_id,
          user_email: user.type === 'ED' ? '' : user.email,
        });

        if (response && response.token) {
          setMetabaseToken(response.token);
        }
      } catch (error) {
        console.error('❌ Error fetching Metabase token:', error);
      } finally {
        setIsLoadingToken(false);
      }
    };

    fetchMetabaseToken();
  }, [selectedResource, user, token]);

  // Handle resource selection change
  const handleResourceChange = (resourceId: string) => {
    const resource = metabaseResources.find(r => r.id.toString() === resourceId);
    if (resource) {
      setSelectedResource(resource);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
        <p className="text-muted-foreground">View analytics and insights</p>
      </div>

      {/* Dashboard Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Analytics Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select 
                value={selectedResource?.id.toString()} 
                onValueChange={handleResourceChange}
                disabled={isLoadingResources || metabaseResources.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a dashboard..." />
                </SelectTrigger>
                <SelectContent>
                  {metabaseResources.map((resource) => (
                    <SelectItem key={resource.id} value={resource.id.toString()}>
                      {resource.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isLoadingToken && (
              <div className="text-sm text-muted-foreground">Loading dashboard...</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Metabase Embed */}
      {metabaseToken && selectedResource && (
        <Card>
          <CardContent className="p-0">
            <iframe 
              src={`https://analytics.hbox.ai/embed/${selectedResource.resource_type}/${metabaseToken}`} 
              width="100%" 
              height="600"
              className="rounded-lg"
              title={selectedResource.name}
            />
          </CardContent>
        </Card>
      )}

      {!metabaseToken && !isLoadingToken && selectedResource && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No dashboard data available
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;